import { authFetch } from './authFetch';

export interface ApiResponse<T> {
  success: boolean;
  statusCode: number;
  message?: string;
  result?: T;
}

export interface TokenResponse {
  token: string;
  refreshToken: string;
  expiresAt: number; // Unix seconds (as returned by the backend)
}

export interface UserInfo {
  id?: string;
  email?: string;
  name?: string;
  /** PRR-316: roles from the canonical server-side source (GET /api/auth/me). Nav visibility uses this; every endpoint independently enforces its own role check server-side. */
  roles?: string[];
  [key: string]: unknown;
}

export const getApiBaseUrl = (): string => {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return '/api';

  const firstDot = host.indexOf('.');
  const apiHost = firstDot > 0 ? `${host.substring(0, firstDot)}.api.${host.substring(firstDot + 1)}` : host;
  return `${window.location.protocol}//${apiHost}`;
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  return await response.json() as T;
};

export const authenticate = async (email: string, password: string): Promise<TokenResponse> => {
  const response = await fetch(`${getApiBaseUrl()}/api/auth/authenticate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  return await parseJsonResponse<TokenResponse>(response);
};

export const refreshToken = async (refreshTokenValue: string): Promise<TokenResponse> => {
  const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
  });

  return await parseJsonResponse<TokenResponse>(response);
};

export const me = async (): Promise<UserInfo> => {
  const response = await authFetch(`${getApiBaseUrl()}/api/auth/me`);
  return await parseJsonResponse<UserInfo>(response);
};
